import { useEffect, useRef, useState } from "react";
import { fabric } from "fabric";
import { baseUrl } from "../../utils/baseUrl";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import { useGlobalContext } from "../../context/GlobalContext";

const FabricLayouts = ({
  layoutData,
  vertices,
  doors,
  windows,
  firePlaces,
  admin,
  idx,
  edit,
  revertLayout,
  setRevertLayout,
  activeLayout,
}) => {
  const {
    setResData,
    revertResData,
    resData,
    setSelectZones,
  } = useGlobalContext();

  useEffect(() => {
    if (!admin) {
      if (revertLayout[activeLayout]) {
        setResData((prevResData) => {
          return prevResData.map((item, index) => {
            if (index === activeLayout) {
              return revertResData[activeLayout];
            } else {
              return item;
            }
          });
        });

        setRevertLayout((prevIsLayoutCanEdit) => {
          const updatedIsLayoutCanEdit = [...prevIsLayoutCanEdit];
          updatedIsLayoutCanEdit[idx] = false;
          return updatedIsLayoutCanEdit;
        });
      }
    }
  }, [revertLayout, admin, activeLayout, revertResData, setResData, setRevertLayout, idx]);
  
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [svgObjects, setSvgObjects] = useState([]);
  const [dummysvgObjects, setDummySvgObjects] = useState([]);

  const isDesktop = useMediaQuery("(min-width:750px)");

  const [canvasHeight, setCanvasHeight] = useState(isDesktop ? 350 : 318);

  const roomWidth = layoutData?.room?.width; 
  const roomHeight = layoutData?.room?.height; 

  const [canvasWidth, setCanvasWidth] = useState(
    isDesktop ? 600 : window.innerWidth - 50
  );

  const scaleX = (canvasWidth - 30) / roomWidth;
  const scaleY = (canvasHeight - 30) / roomHeight;

  const scale = Math.min(scaleX, scaleY);

  useEffect(() => {
    if (!admin) {
      setResData((prevResData) => {
        const updatedResData = [...prevResData];
        updatedResData[idx] = {
          ...updatedResData[idx],
          furniture: updatedResData[idx].furniture.map((item) => {
            const matchingObject = dummysvgObjects.find(
              (obj) => obj.name === item.name
            );
            if (matchingObject) {
              return {
                ...item,
                position: {
                  x: matchingObject.position.x,
                  y: matchingObject.position.y,
                  z: matchingObject.position.z,
                },
                rotation: {
                  x: matchingObject.rotation.x,
                  y: matchingObject.rotation.y,
                  z: matchingObject.rotation.z,
                },
              };
            }
            return item;
          }),
        };
        return updatedResData;
      });
    }
  }, [dummysvgObjects, admin, idx, setResData]);

  useEffect(() => {
    const handleResize = () => {
      setCanvasWidth(isDesktop ? 600 : window.innerWidth - 50);
      setCanvasHeight(isDesktop ? 350 : 318);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isDesktop]);

  const preloadImages = async (furnitureItems) => {
    const imageObjects = await Promise.all(
      furnitureItems?.map(async (item) => {
        try {
          const imageUrl = `${baseUrl}/g${item.topViewImage}`;

          const response = await fetch(imageUrl);
          if (!response.ok) throw new Error("Failed to fetch image");

          const contentType = response.headers.get("content-type");

          if (!contentType || !contentType.includes("image")) {
            throw new Error("Invalid image file");
          }

          return new Promise((resolve, reject) => {
            fabric.Image.fromURL(imageUrl, (img) => {
              if (!img) {
                reject(new Error("Failed to load image"));
                return;
              }

              resolve({
                image: img,
                width:
                  item?.lItems && item?.lItems?.length > 0
                    ? item?.width
                    : item?.variant[0]?.width,
                height:
                  item?.lItems && item?.lItems?.length > 0
                    ? item?.height
                    : item?.variant[0]?.length,
                position: item.position,
                rotation: item.rotation,
                name: item.name,
              });
            });
          });
        } catch (error) {
          console.error("Error loading image:", error);
          return null;
        }
      })
    );

    return imageObjects.filter((imgObj) => imgObj !== null);
  };

  useEffect(() => {
    const fetchData = async () => {

      const loadedImages = await preloadImages(layoutData?.furniture);
      setIsLoading(false);
      setSvgObjects(loadedImages);
      setDummySvgObjects(loadedImages);
    };

    fetchData();
  }, [layoutData?.furniture]);


  useEffect(() => {
    if (!isLoading) {
      const canvas = new fabric.Canvas(canvasRef.current, {
        width: admin ? 318 : canvasWidth,
        height: canvasHeight,
        antiAlias: true, 
        selection: false,
      });

      const scaleX = (admin ? 318 - 30 : canvasWidth - 30) / roomWidth;
      const scaleY = (canvasHeight - 30) / roomHeight;

      const scale = Math.min(scaleX, scaleY);

      const canvasCenterX = canvas.getWidth() / 2;
      const canvasCenterY = canvas.getHeight() / 2;

      canvas.zoomToPoint({ x: canvasCenterX, y: canvasCenterY }, scale);

      let minX = Number.MAX_VALUE;
      let minY = Number.MAX_VALUE;
      let maxX = Number.MIN_VALUE;
      let maxY = Number.MIN_VALUE;

      vertices?.forEach((vertex) => {
        vertex.points.forEach((point) => {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        });
      });

      const boundingBoxWidth = maxX - minX;
      const boundingBoxHeight = maxY - minY;

      const offsetX = canvasCenterX - (minX + boundingBoxWidth / 2);
      const offsetY = canvasCenterY - (minY + boundingBoxHeight / 2);

      const updatedVertices = vertices.map((vertex) => {
        const adjustedPoints = vertex.points.map((point) => ({
          x: point.x + offsetX,
          y: point.y + offsetY,
        }));

        return { points: adjustedPoints };
      });

      updatedVertices.forEach((vertex) => {
        const points = vertex.points.map(({ x, y }) => ({ x, y }));

        const shape = new fabric.Polygon(points, {
          stroke: "#757575",
          strokeWidth: 10 / scale,
          selectable: false,
        });
        canvas.add(shape);

        vertex.points.forEach((point) => {
          const circleRadius = shape.strokeWidth / 2;

          const circle = new fabric.Circle({
            left: point.x - circleRadius - 0.5,
            top: point.y - circleRadius - 0.5,
            radius: circleRadius,
            fill: "#757575",
            selectable: false,
          });

          canvas.add(circle);
        });
      });

      doors.forEach((door) => {
        const rotation = door?.rotation.z || 0;

        const adjustedX = door.position.x + offsetX;
        const adjustedY = door.position.y + offsetY;

        const x1 = adjustedX - door.width / 2;
        const y1 = adjustedY;
        const x2 = adjustedX + door.width / 2;
        const y2 = adjustedY;

        const dottedLine = new fabric.Line([x1, y1, x2, y2], {
          stroke: "red",
          strokeWidth: 2 / scale,
          strokeDashArray: [5, 5], 
          selectable: false,
          originX: "center",
          originY: "center",
          id: door.id,
          zIndex: 1,
        });

        dottedLine.rotate(rotation * (180 / Math.PI));

        canvas.add(dottedLine);
        canvas.renderAll();

      });

      windows.forEach((window) => {
        const rotation = window?.rotation.z || 0;
        fabric.Image.fromURL(window.image, (img) => {
          let scaleX = window.width / img.width;
          let scaleY = window.height / img.height;

          const adjustedX = window.position.x + offsetX;
          const adjustedY = window.position.y + offsetY;

          img.set({
            scaleX,
            scaleY,
            left: adjustedX,
            top: adjustedY,
            originX: "center",
            originY: "center",
            selectable: false,
            id: window.id,
            imageSmoothing: false, 
            type: window.type,
            zIndex: 1,
          });
          img.rotate(rotation * (180 / Math.PI));
          canvas.add(img);
        });
      });

      firePlaces.forEach((firePlace) => {
        const rotation = firePlace?.rotation.z || 0;
        fabric.Image.fromURL(firePlace.image, (img) => {
          let scaleX = firePlace.width / img.width;
          let scaleY = firePlace.height / img.height;

          const adjustedX = firePlace.position.x + offsetX;
          const adjustedY = firePlace.position.y + offsetY;

          img.set({
            scaleX,
            scaleY,
            left: adjustedX,
            top: adjustedY,
            originX: "center",
            originY: "center",
            selectable: false,
            id: firePlace.id,
            imageSmoothing: false, 
            type: firePlace.type,
            zIndex: 2,
          });
          img.rotate(rotation * (180 / Math.PI));
          canvas.add(img);
        });
      });

      const clockwiseIconPath =
        '<path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/><path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>';
      const counterclockwiseIconPath =
        '<path fill-rule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2z"/><path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466"/>';
      const verticalFlipIconPath = `   <path d="M7 2.5a.5.5 0 0 0-.939-.24l-6 11A.5.5 0 0 0 .5 14h6a.5.5 0 0 0 .5-.5zm2.376-.484a.5.5 0 0 1 .563.245l6 11A.5.5 0 0 1 15.5 14h-6a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .376-.484M10 4.46V13h4.658z"/>`;
      const horizontalFlipIconPath = `     <path d="M13.5 7a.5.5 0 0 0 .24-.939l-11-6A.5.5 0 0 0 2 .5v6a.5.5 0 0 0 .5.5zm.485 2.376a.5.5 0 0 1-.246.563l-11 6A.5.5 0 0 1 2 15.5v-6a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 .485.376M11.539 10H3v4.658z"/>`;
      const Box = `<path d="M 5 0 H 95 A 5 5 0 0 1 100 5 V 20 A 5 5 0 0 1 95 25 H 5 A 5 5 0 0 1 0 20 V 5 A 5 5 0 0 1 5 0 Z"  />
`;

      console.log('🪑 fabricLayouts: Rendering furniture');
      console.log('  offsetX:', offsetX, 'offsetY:', offsetY);
      console.log('  canvas size:', canvas?.getWidth(), 'x', canvas?.getHeight());
      
      svgObjects.forEach(
        ({ image, width, height, position, rotation, name }, idx) => {
          // Use same coordinate system as doors/windows/fireplaces
          // Furniture positions are in absolute room coordinates
          const canvasX = position.x + offsetX;
          const canvasY = -position.y + offsetY; // Y-axis is inverted for furniture

          if (idx === 0) {
            console.log('  furniture[0]:', name);
            console.log('    position:', position);
            console.log('    canvasX:', canvasX, 'canvasY:', canvasY);
            console.log('    width:', width, 'height:', height);
          }

          image.set({
            left: canvasX,
            top: canvasY,
            scaleX: (width - 8) / image.width,
            scaleY: (height - 8) / image.height,
            selectable: edit,
            originX: "center",
            originY: "center",
            hasControls: false,
            zIndex: -1,
            name: name,
          });

          const radians = rotation.z;
          const degrees = radians * (180 / Math.PI);
          image.rotate(degrees);

          canvas.add(image);
        }
      );

      canvas.on("object:modified", (options) => {
        const modifiedObject = options.target;

        const object = dummysvgObjects.find(
          (item) => item.svgGroup === modifiedObject
        );

        const radians = object.rotation.z;
        const degrees = radians * (180 / Math.PI);

        let currentRotation = degrees * -1;

        let dummyRotation;

        const origionalPosition =
          revertResData[activeLayout]?.furniture?.find(
            (item) => item.name === object?.name
          )?.rotation.z *
          (180 / Math.PI);

        canvas.getObjects().forEach((obj) => {
          if (obj.isControlButton) {
            canvas.remove(obj);
          }
        });

        const canvasX = modifiedObject.left;
        const canvasY = modifiedObject.top;

        const BoxDesign = new fabric.Path(Box, {
          width: 100,
          height: 28,
          fill: "whitesmoke",
          originX: "center",
          originY: "center",
          selectable: false,
          hoverCursor: "pointer",
          isEdit: true,
          isControlButton: true,
          zIndex: -1, 
        });

        const rotateClockwiseButton = new fabric.Path(clockwiseIconPath, {
          width: 20,
          height: 20,
          fill: "black",
          originX: "center",
          originY: "center",
          selectable: false,
          hoverCursor: "pointer",
          isEdit: true,
          isControlButton: true,
          zIndex: -1, 
        });

        const rotateCounterClockwiseButton = new fabric.Path(
          counterclockwiseIconPath,
          {
            width: 20,
            height: 20,
            fill: "black",
            originX: "center",
            originY: "center",
            selectable: false,
            hoverCursor: "pointer",
            isEdit: true,
            isControlButton: true,
            zIndex: -1, 
          }
        );
        const verticalFlipButton = new fabric.Path(verticalFlipIconPath, {
          width: 20,
          height: 20,
          fill: "black",
          originX: "center",
          originY: "center",
          selectable: false,
          hoverCursor: "pointer",
          isEdit: true,
          isControlButton: true,
          zIndex: -1, 
        });

        const horizontalFlipButton = new fabric.Path(horizontalFlipIconPath, {
          width: 20,
          height: 20,
          fill: "black",
          originX: "center",
          originY: "center",
          selectable: false,
          hoverCursor: "pointer",
          isEdit: true,
          isControlButton: true,
          zIndex: -1, 
        });

        rotateClockwiseButton.left = canvasX - 10;
        rotateClockwiseButton.top = canvasY;

        rotateCounterClockwiseButton.left = canvasX + 10;
        rotateCounterClockwiseButton.top = canvasY;

        verticalFlipButton.left = canvasX - 30;
        verticalFlipButton.top = canvasY;

        horizontalFlipButton.left = canvasX + 30;
        horizontalFlipButton.top = canvasY;
        BoxDesign.left = canvasX;
        BoxDesign.top = canvasY;
        rotateClockwiseButton.on("mousedown", () => {
          dummyRotation = currentRotation - 90 * 3;
          currentRotation = currentRotation + 90; 

          if (dummyRotation === origionalPosition * -1) {
            currentRotation = origionalPosition * -1;
          }

          const newAngle = currentRotation;
          modifiedObject.rotate(newAngle); 

          setDummySvgObjects((prevSvgObjects) => {
            const updatedSvgObjects = prevSvgObjects.map((obj) => {
              if (obj.svgGroup === modifiedObject) {
                const newAngleRadians = (newAngle * Math.PI) / 180;

                const newAngleRadiansWithSign =
                  newAngleRadians >= 0
                    ? -Math.abs(newAngleRadians)
                    : Math.abs(newAngleRadians);
                return {
                  ...obj,
                  rotation: {
                    ...obj.rotation,
                    z: newAngleRadiansWithSign,
                  },
                };
              }
              return obj;
            });
            return updatedSvgObjects;
          });
        });

        rotateCounterClockwiseButton.on("mousedown", () => {
          dummyRotation = currentRotation + 90 * 3;
          currentRotation = currentRotation - 90; 

          if (dummyRotation === origionalPosition * -1) {
            currentRotation = origionalPosition * -1;
          }

          const newAngle = currentRotation;
          modifiedObject.rotate(newAngle);

          setDummySvgObjects((prevSvgObjects) => {
            const updatedSvgObjects = prevSvgObjects.map((obj) => {
              if (obj.svgGroup === modifiedObject) {
                const newAngle = currentRotation;
                const newAngleRadians = (newAngle * Math.PI) / 180;
                const newAngleRadiansWithSign =
                  newAngleRadians >= 0
                    ? -Math.abs(newAngleRadians)
                    : Math.abs(newAngleRadians);

                return {
                  ...obj,
                  rotation: {
                    ...obj.rotation,
                    z: newAngleRadiansWithSign,
                  },
                };
              }
              return obj;
            });
            return updatedSvgObjects;
          });
        });
        verticalFlipButton.on("mousedown", () => {
          modifiedObject.flipY = !modifiedObject.flipY;

          modifiedObject.setCoords();

          canvas.renderAll();

          setDummySvgObjects((prevSvgObjects) => {
            const updatedSvgObjects = prevSvgObjects.map((obj) => {
              if (obj.svgGroup === modifiedObject) {
                return {
                  ...obj,
                  flipY: modifiedObject.flipY,
                };
              }
              return obj; 
            });
            return updatedSvgObjects; 
          });

        });

        horizontalFlipButton.on("mousedown", () => {
          modifiedObject.flipX = !modifiedObject.flipX;

          modifiedObject.setCoords();

          canvas.renderAll();

          setDummySvgObjects((prevSvgObjects) => {
            const updatedSvgObjects = prevSvgObjects.map((obj) => {
              if (obj.svgGroup === modifiedObject) {
                return {
                  ...obj,
                  flipX: modifiedObject.flipX,
                };
              }
              return obj; 
            });
            return updatedSvgObjects; 
          });

        });

        if (edit) {
          canvas.add(BoxDesign);
          canvas.add(
            rotateClockwiseButton,
            rotateCounterClockwiseButton,
            verticalFlipButton,
            horizontalFlipButton
          );
        }
      });

      function updateSelectedZones(
        activeLayout,
        selectZones,
        resData,
        modifiedObject
      ) {
        const furnitureMatch = resData[activeLayout].furniture.find(
          (furniture) => furniture.name === modifiedObject.name
        );

        if (furnitureMatch) {
          const newSelectedZones = selectZones.map((zones, index) => {
            if (index === activeLayout) {
              return zones.filter((zone) => zone !== furnitureMatch._id);
            }
            return zones;
          });

          return newSelectedZones;
        }

        return selectZones;
      }
      canvas.on("object:modified", (options) => {
        const modifiedObject = options.target;

        setDummySvgObjects((prevSvgObjects) => {
          const updatedSvgObjects = prevSvgObjects.filter((obj) => {
            if (obj.svgGroup === modifiedObject) {
              if (
                modifiedObject.left < 0 ||
                modifiedObject.top < 0 ||
                modifiedObject.left > canvas.getWidth() ||
                modifiedObject.top > canvas.getHeight()
              ) {

                setSelectZones((prevSelectedZones) =>
                  updateSelectedZones(
                    activeLayout,
                    prevSelectedZones,
                    resData,
                    modifiedObject
                  )
                );

                setResData((prevResData) => {
                  const newResData = [...prevResData];

                  if (
                    newResData?.[activeLayout] &&
                    newResData?.[activeLayout].furniture
                  ) {
                    newResData[activeLayout].furniture = newResData?.[
                      activeLayout
                    ]?.furniture?.filter(
                      (item) => item.name !== modifiedObject.name
                    );
                  }

                  return newResData;
                });

                return false;
              } else {
                // Convert canvas coordinates back to room coordinates
                // Inverse of: canvasX = position.x + offsetX, canvasY = -position.y + offsetY
                const newX = modifiedObject.left - offsetX;
                const newY = -(modifiedObject.top - offsetY); // Invert Y back

                obj.position = {
                  x: newX,
                  y: newY,
                  z: 0,
                };
                obj.width = modifiedObject.width * modifiedObject.scaleX; 
                obj.height = modifiedObject.height * modifiedObject.scaleY; 
              }
            }
            return true;
          });
          return updatedSvgObjects;
        });
      });
    }
  }, [isLoading, svgObjects, canvasWidth, canvasHeight, activeLayout, admin, doors, dummysvgObjects, edit, firePlaces, resData, revertResData, roomHeight, roomWidth, setResData, setSelectZones, vertices, windows]);

  return (
    <>
      <div
        style={{
          width: admin ? 318 : canvasWidth,
          height: canvasHeight,
          margin: "0px auto",
          overflow: "visible",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Skeleton
              variant="rectangular"
              width={roomWidth * scale}
              height={roomHeight * scale}
              sx={{ my: "auto" }}
            />
          </Box>
        ) : (
          <canvas ref={canvasRef} />
        )}
      </div>
    </>
  );
};

export default FabricLayouts;
